import BTButton from '@common-components/bt-button/bt-button';
import FilesIcons from '@common-components/files-icons/filesIcons';
import { Box, CircularProgress, Grid, IconButton, Stack, Tooltip, Typography, Menu, MenuItem, Dialog, DialogContent, ToggleButton, ToggleButtonGroup, Theme } from '@mui/material';
import { _TasksExtraDataFilesThunks } from "@common-reducers/DBServiceThunks";
import { mediaManager } from '@common-reducers/MediaManagerReducer';
import { pickerDialogActions } from '@common-reducers/PickerDialogReducer';
import { getExtraDataMergedFiles } from '@common-reducers/TaskSelectors';
import { createOrUpdateTaskExtraDataFile, deleteTaskExtraDataFile, deleteTaskColumnFile, deleteUpdateFile } from '@common-reducers/TaskThunks';
import type { FileItem } from '@common-models/ExtraDataItem';
import { handleUpload } from '@common-services/upload';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { namespace } from '@common/config/constants';
import FileSaver from 'file-saver';
import { loadJszip } from '@extension/utils/lazy-lib';
import JSZipUtils from "jszip-utils";
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillFileZip } from 'react-icons/ai';
import { IoCloudUploadOutline } from 'react-icons/io5';
import { BsThreeDotsVertical, BsTrash, BsDownload, BsEye, BsGrid, BsListUl } from 'react-icons/bs';

const titleStyle = {
    fontSize: "14px",
    fontWeight: 500,
    color: 'var(--color-text)',
    marginBottom: '16px',
    marginTop: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&::before': {
        content: '""',
        width: '3px',
        height: '16px',
        backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? 'var(--color-surface)40' : '#c3c6d4',
        borderRadius: '2px',
        display: 'inline-block'
    }
}

const dropZoneStyle = {
    minHeight: "200px",
    border: "2px dashed",
    borderColor: 'divider',
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "action.hover",
    transition: "all 0.3s ease",
    cursor: "pointer",
    '&:hover': {
        borderColor: "primary.main",
        backgroundColor: "action.hover"
    }
}

const activeDropZoneStyle = {
    borderColor: "primary.main",
    backgroundColor: "action.hover"
}

const fileItemGridStyle = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? 'background.default' : 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    height: '200px',
    width: '200px',
    minWidth: '180px',
    maxWidth: '200px',
    '&:hover': {
        backgroundColor: 'action.hover',
        boxShadow: 1
    }
}

const fileItemListStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 16px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? 'background.default' : 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    '&:hover': {
        backgroundColor: 'action.hover',
        boxShadow: 1
    }
}

const filePreviewStyle = {
    width: '100%',
    height: '240px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-surface)',
    borderRadius: '4px',
    overflow: 'hidden',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block'
    }
}

const isImageFile = (file: any) => {
    const mimeType = String(file?.type || file?.mimeType || '').toLowerCase();
    const filename = String(file?.filename || file?.name || '').toLowerCase();
    const url = String(file?.url || '').toLowerCase();
    return (
        mimeType.includes('image') ||
        /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/.test(filename) ||
        /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/.test(url)
    );
};

const FileThumbnail = ({ file, iconSize = 60, imgStyle }: { file: any; iconSize?: number; imgStyle?: React.CSSProperties }) => {
    const [showAsImage, setShowAsImage] = useState(Boolean(file?.url));

    useEffect(() => {
        // Try rendering as image whenever URL changes; fallback to icon on load error.
        setShowAsImage(Boolean(file?.url));
    }, [file?.url]);

    if (showAsImage && file?.url) {
        return (
            <img
                src={file.url}
                alt={file?.filename || file?.name || 'file'}
                style={imgStyle}
                onError={() => setShowAsImage(false)}
                loading="lazy"
            />
        );
    }

    return <FilesIcons style={{ width: iconSize, height: iconSize }} file={file} />;
};

type PreviewFile = { url?: string; filename?: string; type?: string } | null;

const PreviewDialog = ({ open, onClose, file }: { open: boolean; onClose: () => void; file: PreviewFile }) => {
    const [isLoading, setIsLoading] = useState(true);
    
    useEffect(() => {
        if (open) {
            setIsLoading(true);
        }
    }, [open]);

    const handleImageLoad = () => {
        setIsLoading(false);
    };

    if (!file) return null;

    const isImage = isImageFile(file);
    
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogContent sx={{ position: 'relative', minHeight: '300px' }}>
                {isLoading && (
                    <Box sx={{ 
                        position: 'absolute', 
                        top: '50%', 
                        left: '50%', 
                        transform: 'translate(-50%, -50%)'
                    }}>
                        <CircularProgress />
                    </Box>
                )}
                {isImage ? (
                    <img 
                        src={file.url} 
                        alt={file.filename}
                        style={{ 
                            width: '100%', 
                            height: 'auto', 
                            maxHeight: '80vh', 
                            objectFit: 'contain',
                            display: isLoading ? 'none' : 'block'
                        }}
                        onLoad={handleImageLoad}
                    />
                ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <FilesIcons style={{ width: 120, height: 120 }} file={file} />
                        <Typography variant="h6" sx={{ mt: 2 }}>{file.filename}</Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

const FileMenu = ({ fileIndex, fileCategory, onClose, anchorEl, file }: { fileIndex: number; fileCategory: string; onClose: () => void; anchorEl: Element | null; file: PreviewFile }) => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const [previewOpen, setPreviewOpen] = useState(false);

    const handleDelete = () => {
        onClose();
        if (fileCategory === 'general') {
            dispatch(deleteTaskExtraDataFile({ fileIndex }));
        } else if (fileCategory === 'tasks' && file?.url) {
            dispatch(deleteTaskColumnFile({ fileUrl: file.url }));
        } else if (fileCategory === 'updates' && file?.url) {
            dispatch(deleteUpdateFile({ fileUrl: file.url }));
        }
    };

    const handleDownload = () => {
        if (file && file.url) {
            JSZipUtils.getBinaryContent(file.url, function (err, data) {
                if (err) {
                    console.error(err);
                    return;
                }
                const blob = new Blob([data], { type: file.type });
                FileSaver.saveAs(blob, file.filename);
            });
        }
        onClose();
    };

    const handlePreview = () => {
        setPreviewOpen(true);
    };

    const handlePreviewClose = () => {
        setPreviewOpen(false);
        onClose();
    };

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={onClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handlePreview}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <BsEye size={16} />
                        <Typography>Preview</Typography>
                    </Stack>
                </MenuItem>
                <MenuItem onClick={handleDownload}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <BsDownload size={16} />
                        <Typography>Download</Typography>
                    </Stack>
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <BsTrash size={16} />
                        <Typography>Delete</Typography>
                    </Stack>
                </MenuItem>
            </Menu>
            <PreviewDialog
                open={previewOpen}
                onClose={handlePreviewClose}
                file={file}
            />
        </>
    );
};

export const ExtraDataFiles = () => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const totalFiles: any = useAppSelector(getExtraDataMergedFiles);
    const allFiles = [totalFiles.tasks, totalFiles.updates, totalFiles.generalFiles].flat();
    const hasAnyFiles = Boolean(
        totalFiles?.tasks?.length ||
        totalFiles?.updates?.length ||
        totalFiles?.generalFiles?.length
    );
    const [menuAnchorEl, setMenuAnchorEl] = useState<Element | null>(null);
    const [selectedFile, setSelectedFile] = useState<{ index: number; category: string; file: PreviewFile }>({ index: -1, category: '', file: null });
    const [isDragging, setIsDragging] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleFileButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleMenuClick = (event: React.MouseEvent, index: number, category: string, file: PreviewFile) => {
        event.stopPropagation();
        setSelectedFile({ index, category, file });
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedFile({ index: -1, category: '', file: null });
    };

    const uploadFile = async (base64file: string, mimeType: string) => {
        try {
            const response: any = await handleUpload(base64file, mimeType);
            if (response.uploadResponse.url) {
                return response.image_url;
            }
        } catch (err) {
            console.log(err);
        }
    };

    const uploadFiles = useCallback(async (files: File[]) => {
        if (files.length) {
            const promises = files.map((file) => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = async () => {
                        const base64file = reader.result;
                        const urlLink = await uploadFile(base64file?.toString() ?? '', file.type);
                        const fileDetails = {
                            url: urlLink,
                            type: file.type,
                            filename: file.name,
                        };
                        resolve(fileDetails);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
            });
            return Promise.all(promises);
        }
    }, []);

    function openMediaManager(e: React.MouseEvent, i: number, fileCategory: string) {
        e.stopPropagation();

        let relativeIndex = i;
        if (fileCategory === 'updates') {
            relativeIndex += totalFiles.tasks.length;
        } else if (fileCategory === 'generalFiles') {
            relativeIndex += totalFiles.tasks.length + totalFiles.updates.length;
        }

        const currentFilesAndIndex: Record<string, unknown> = {}
        currentFilesAndIndex["index"] = relativeIndex;
        currentFilesAndIndex["shown"] = true;
        currentFilesAndIndex["filesTotal"] = allFiles

        currentFilesAndIndex["isViewOnly"] = true;

        dispatch(
            mediaManager.setSelectedCellFilesAndCurrentindex(currentFilesAndIndex)
        );

        dispatch(pickerDialogActions.closePickerDialog());
    }

    const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsLoading(true);
        const selectedFiles = Array.from(event.target.files || []);
       
        const uploadedFilesDetails = await uploadFiles(selectedFiles);
        await dispatch(createOrUpdateTaskExtraDataFile((uploadedFilesDetails ?? []) as FileItem[]))
        setIsLoading(false);
    };

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        setIsLoading(true);
        setIsDragging(false);
        event.preventDefault();
        event.stopPropagation();
        
        if (event.dataTransfer.items) {
            event.dataTransfer.items.clear();
        }
        
        const droppedFiles = Array.from(event.dataTransfer.files || []);
        const uploadedFilesDetails = await uploadFiles(droppedFiles);
        await dispatch(createOrUpdateTaskExtraDataFile((uploadedFilesDetails ?? []) as FileItem[]))

        setIsLoading(false);
    };

    async function downloadZip() {
        const JSZip = await loadJszip();
        const zip = new JSZip();
        let count = 0;
        const allFiles = [totalFiles.tasks, totalFiles.updates, totalFiles.generalFiles].flat();

        allFiles.forEach(function (file) {
            var filename = file.filename;
            JSZipUtils.getBinaryContent(file.url, function (err, data) {
                if (err) {
                    throw err;
                }
                zip.file(filename, data, { binary: true });
                count++;
                if (count === allFiles.length) {
                    zip.generateAsync({ type: 'blob' }).then(function (content) {
                        FileSaver.saveAs(content, 'download.zip');
                    });
                }
            });
        });
    }

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
        setTimeout(() => {
            setIsDragging(false);
        }, 4000);
    };

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: string) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    const renderFileItemGrid = (file: any, index: number, category: string) => (
        <Grid item xs={6} key={`${category}_${index}`} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={fileItemGridStyle}>
                <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                    <IconButton
                        size="small"
                        sx={(theme) => ({
                            backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
                            color: 'text.primary',
                            boxShadow: 1,
                            '&:hover': { 
                                backgroundColor: theme.palette.mode === 'dark' ? 'action.hover' : 'background.paper'
                            }
                        })}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClick(e, index, category, file);
                        }}
                    >
                        <BsThreeDotsVertical size={14} />
                    </IconButton>
                </Box>
                <Box 
                    sx={{
                        ...filePreviewStyle,
                        height: '120px',
                        cursor: 'pointer',
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                    }} 
                    onClick={(e) => {
                        e.stopPropagation();
                        openMediaManager(e, index, category);
                    }}
                >
                    <FileThumbnail file={file} iconSize={60} />
                </Box>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        mt: 1,
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'text.primary'
                    }}
                >
                    {file.filename}
                </Typography>
                <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ mt: 'auto' }}
                >
                    {file.type}
                </Typography>
            </Box>
        </Grid>
    );

    const renderFileItemList = (file: any, index: number, category: string) => (
        <Grid item xs={12} key={`${category}_${index}`}>
            <Box sx={fileItemListStyle}>
                <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
                    borderRadius: '4px',
                    border: '1px solid',
                    borderColor: 'divider',
                    padding: '4px'
                }}>
                    <FileThumbnail
                        file={file}
                        iconSize={40}
                        imgStyle={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: 'text.primary',
                            fontWeight: 500
                        }}
                    >
                        {file.filename}
                    </Typography>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: 'text.secondary',
                            display: 'block',
                            mt: 0.5
                        }}
                    >
                        {file.type}
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    sx={(theme) => ({
                        backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
                        color: 'text.primary',
                        boxShadow: 1,
                        '&:hover': { 
                            backgroundColor: theme.palette.mode === 'dark' ? 'action.hover' : 'background.paper'
                        }
                    })}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e, index, category, file);
                    }}
                >
                    <BsThreeDotsVertical size={14} />
                </IconButton>
            </Box>
        </Grid>
    );

    const renderFileSection = (files: any[], category: string) => {
        if (!files?.length) return null;
        
        return (
            <>
                <Typography sx={titleStyle}>
                    {t(`extraDataFiles.${category}Files`)}
                </Typography>
                <Grid 
                    container 
                    spacing={2}
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        margin: 0,
                        width: '100%',
                        justifyContent: 'flex-start'
                    }}
                >
                    {files.map((file, index) => 
                        viewMode === 'grid' 
                            ? renderFileItemGrid(file, index, category)
                            : renderFileItemList(file, index, category)
                    )}
                </Grid>
            </>
        );
    };

    return (
        <Stack
            mt={2}
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{ minHeight: "300px", padding: '1rem', position: "relative" }}
        >
            <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <BTButton onClick={() => fileInputRef.current?.click()}>
                    {t('extraDataFiles.dropHereToUpload')}
                </BTButton>

                <Stack direction="row" spacing={1} alignItems="center">
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                    sx={(theme) => ({
                        backgroundColor: theme.palette.mode === 'dark' ? '#1c1f3b' : '#f5f6f8',
                        padding: '4px',
                        borderRadius: '4px',
                        '& .MuiToggleButton-root': {
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            minWidth: '32px',
                            color: theme.palette.mode === 'dark' ? 'var(--color-surface)80 !important' : '#676879 !important',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'var(--color-surface)14' : 'var(--color-text)0a',
                            },
                            '&.Mui-selected': {
                                backgroundColor: 'var(--color-surface) !important',
                                color: 'var(--color-text) !important',
                                boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 2px 6px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'var(--color-surface)1f' : 'var(--color-surface)',
                                }
                            }
                        }
                    })}
                >
                <ToggleButton value="grid" aria-label="grid view">
                    <BsGrid size={18} />
                </ToggleButton>
                <ToggleButton value="list" aria-label="list view">
                    <BsListUl size={18} />
                </ToggleButton>
                </ToggleButtonGroup>

                    {allFiles && allFiles.length > 1 && (
                        <Tooltip 
                            title={`${t('extraDataFiles.download')} ${allFiles.length} ${t('extraDataFiles.filesZip')}`} 
                            arrow 
                            placement="top" 
                            disableInteractive
                        >
                            <IconButton 
                                size="small" 
                                onClick={downloadZip}
                                sx={(theme) => ({
                                    backgroundColor: theme.palette.mode === 'dark' ? '#1c1f3b' : '#f5f6f8',
                                    color: theme.palette.mode === 'dark' ? 'var(--color-surface)80' : '#676879',
                                    padding: '8px',
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark' ? 'var(--color-surface)14' : 'var(--color-text)0a',
                                        color: theme.palette.mode === 'dark' ? 'var(--color-surface)' : '#323338',
                                    }
                                })}
                            >
                                <AiFillFileZip size={18} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            {isLoading ? (
                <Stack sx={{ height: "250px" }} justifyContent="center" alignItems="center">
                    <CircularProgress />
                </Stack>
            ) : (
                <Stack spacing={3}>
                    {(!hasAnyFiles || isDragging) ? (
                        <Box 
                            sx={{
                                ...dropZoneStyle,
                                ...(isDragging && activeDropZoneStyle)
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <IoCloudUploadOutline size={48} color="#53BDEB" style={{ marginBottom: '16px' }} />
                            <Typography variant="body1" color="var(--color-text)" sx={{ mb: 1 }}>
                                {isDragging ? t('extraDataFiles.dropHereToUpload') : t('extraDataFiles.dragAndDropFiles')}
                            </Typography>
                            <Typography variant="body2" color="var(--color-text)">
                                {t('extraDataFiles.orClickToUpload')}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {renderFileSection(totalFiles.tasks, 'tasks')}
                            {renderFileSection(totalFiles.updates, 'updates')}
                            {renderFileSection(totalFiles.generalFiles, 'general')}
                        </>
                    )}
                </Stack>
            )}

            <FileMenu
                fileIndex={selectedFile.index}
                fileCategory={selectedFile.category}
                file={selectedFile.file}
                anchorEl={menuAnchorEl}
                onClose={handleMenuClose}
            />

            <input 
                ref={fileInputRef}
                type="file" 
                hidden 
                multiple 
                onChange={handleFileInputChange} 
            />
        </Stack>
    );
}
